import type { ExprAst, FieldRef } from '../../schema/expr'
import type { AnswerValue } from '../../schema/answers'
import { isAnswered, asStringArray } from '../../schema/answers'

export type ExprLookup = (ref: FieldRef) => AnswerValue | undefined

function toComparable(value: AnswerValue | undefined): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) return asStringArray(value).join(',')
  return ''
}

function toNumber(value: AnswerValue | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return null
    const num = Number(trimmed)
    return Number.isFinite(num) ? num : null
  }
  return null
}

/** ประเมิน AST — ไม่มีการ parse ข้อความตอนรัน */
export function evaluateExpr(ast: ExprAst, lookup: ExprLookup): boolean {
  switch (ast.kind) {
    case 'literal':
      return ast.value
    case 'and':
      return evaluateExpr(ast.left, lookup) && evaluateExpr(ast.right, lookup)
    case 'or':
      return evaluateExpr(ast.left, lookup) || evaluateExpr(ast.right, lookup)
    case 'not':
      return !evaluateExpr(ast.expr, lookup)
    case 'answered':
      return isAnswered(lookup(ast.ref))
    case 'compare': {
      const actual = lookup(ast.ref)
      if (ast.op === 'contains') {
        const list = asStringArray(actual)
        const needle = String(ast.value).trim()
        if (list.length > 0) return list.some((item) => item.trim() === needle)
        return toComparable(actual).includes(needle)
      }
      if (typeof ast.value === 'number' || ast.op === 'lt' || ast.op === 'lte' || ast.op === 'gt' || ast.op === 'gte') {
        const left = toNumber(actual)
        const right = typeof ast.value === 'number' ? ast.value : toNumber(ast.value)
        if (left === null || right === null) return false
        switch (ast.op) {
          case 'eq':
            return left === right
          case 'ne':
            return left !== right
          case 'lt':
            return left < right
          case 'lte':
            return left <= right
          case 'gt':
            return left > right
          case 'gte':
            return left >= right
          default:
            return false
        }
      }
      const left = toComparable(actual)
      const right = String(ast.value).trim()
      if (ast.op === 'eq') return left === right
      if (ast.op === 'ne') return left !== right
      return false
    }
  }
}
